import type { Command } from "commander";
import type { ResolvedConfig } from "../config.js";
import { CliApiClient } from "../api/cli-api.js";
import { browserPkceLogin } from "../auth/browser-login.js";
import {
  credentialsFromTokenResponse,
  loadCredentials,
  saveCredentials,
  clearCredentials,
} from "../auth/credentials.js";
import { finalizeLogin, refreshStoredSession } from "../auth/session.js";
import { AspError } from "../utils/errors.js";
import { printJson, printSuccess, printInfo, formatDateTime, printTable } from "../utils/output.js";
import { promptCredentials, promptLoginMethod } from "../utils/prompt.js";
import { formatPlatformLabel, requireCredentials } from "../utils/platform.js";

interface LoginOptions {
  browser?: boolean;
  password?: boolean;
  token?: string;
  account?: string;
  pass?: string;
}

const LOGIN_OPTIONS: Array<[string, string]> = [
  ["--browser", "Login via browser authorization (opens /cli/login, PKCE)"],
  ["--password", "Login with email and password"],
  ["--token <token>", "Save and validate an existing access token"],
  ["--account <email>", "Account email (non-interactive, use with --pass)"],
  ["--pass <password>", "Account password (non-interactive, use with --account)"],
];

function wantsPasswordLogin(opts: LoginOptions): boolean {
  return !!(opts.password || opts.account || opts.pass);
}

async function ensureBrowserLoginEnabled(config: ResolvedConfig): Promise<void> {
  const meta = await new CliApiClient(config).getMeta().catch(() => null);
  if (meta?.features?.browser_pkce_login === false) {
    throw new AspError("Browser authorization login is not enabled on this platform.", {
      code: "FEATURE_DISABLED",
      hint: "Use `asp auth login --password` instead.",
    });
  }
}

async function runPasswordLogin(
  config: ResolvedConfig,
  opts: Pick<LoginOptions, "account" | "pass">,
): Promise<void> {
  const { account, password } = await promptCredentials({
    account: opts.account,
    password: opts.pass,
  });

  const loginResponse = await new CliApiClient(config).login(account, password);
  await finalizeLogin(config, loginResponse);

  const me = await new CliApiClient(config).getMe();
  const display = me.user?.email ?? me.email ?? me.user?.display_name ?? me.account ?? account;
  printSuccess(`Logged in as ${display} (${formatPlatformLabel(config.platformUrl)})`);
}

async function runBrowserLogin(config: ResolvedConfig): Promise<void> {
  await ensureBrowserLoginEnabled(config);
  await browserPkceLogin(config);
}

async function runLogin(config: ResolvedConfig, opts: LoginOptions): Promise<void> {
  if (opts.token) {
    await saveTokenLogin(config, opts.token);
    return;
  }

  if (opts.browser && wantsPasswordLogin(opts)) {
    throw new AspError("Cannot use --browser together with --password, --account, or --pass.", {
      code: "INVALID_REQUEST",
      hint: "Choose one login method.",
    });
  }

  if (opts.browser) {
    await runBrowserLogin(config);
    return;
  }

  if (wantsPasswordLogin(opts)) {
    await runPasswordLogin(config, opts);
    return;
  }

  if (!process.stdin.isTTY) {
    throw new AspError("Non-interactive login requires a login method flag.", {
      code: "INVALID_REQUEST",
      hint: "Use `asp auth login --browser` or `asp auth login --password --account <email> --pass <password>`.",
    });
  }

  const method = await promptLoginMethod();
  if (method === "browser") {
    await runBrowserLogin(config);
    return;
  }

  await runPasswordLogin(config, opts);
}

async function saveTokenLogin(config: ResolvedConfig, token: string): Promise<void> {
  const creds = credentialsFromTokenResponse(config.platformUrl, {
    access_token: token,
    token_type: "Bearer",
  });
  await saveCredentials(creds);
  const me = await new CliApiClient({ ...config, token }).getMe();
  const display =
    me.user?.email ?? me.email ?? me.user?.display_name ?? me.account ?? me.user_id;
  printSuccess(`Token saved. Logged in as ${display} (${formatPlatformLabel(config.platformUrl)})`);
}

async function runStatus(getConfig: () => ResolvedConfig): Promise<void> {
  const base = getConfig();
  const creds = await loadCredentials();

  if (!base.token && !creds) {
    printInfo("Not logged in.");
    printInfo("Run `asp auth login` to sign in.");
    return;
  }

  const { config: apiConfig } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(apiConfig);
  const me = await client.getMe();
  const latestCreds = (await loadCredentials()) ?? creds;

  if (base.json) {
    printJson({
      logged_in: true,
      environment: formatPlatformLabel(apiConfig.platformUrl),
      user: me.user ?? {
        id: me.user_id,
        email: me.email,
        display_name: me.username ?? me.account,
      },
      scopes: me.scopes,
      expires_at: latestCreds?.expires_at,
      platform_url: apiConfig.platformUrl,
    });
    return;
  }

  const user = me.user ?? {
    id: me.user_id ?? "",
    email: me.email,
    display_name: me.username ?? me.account,
  };
  printInfo(`Environment: ${formatPlatformLabel(apiConfig.platformUrl)}`);
  printInfo(`User: ${user.display_name ?? user.email ?? user.id}`);
  if (user.email) printInfo(`Email: ${user.email}`);
  if (me.scopes?.length) printInfo(`Scopes: ${me.scopes.join(", ")}`);
  if (latestCreds?.expires_at) {
    printInfo(`Token expires: ${formatDateTime(latestCreds.expires_at)}`);
  }
}

async function runRefresh(getConfig: () => ResolvedConfig): Promise<void> {
  const base = getConfig();
  const { config, creds } = await requireCredentials(base, base.platformUrl);

  if (!creds.refresh_token) {
    throw new AspError("No refresh token available.", {
      code: "UNAUTHORIZED",
      hint: "Run `asp auth login`.",
      exitCode: 3,
    });
  }

  await refreshStoredSession(config, creds.refresh_token, config.platformUrl);
  printSuccess(`Token refreshed (${formatPlatformLabel(config.platformUrl)}).`);
}

async function runLogout(getConfig: () => ResolvedConfig): Promise<void> {
  const base = getConfig();
  const platformUrl = base.platformUrl;
  const creds = await loadCredentials();

  if (!creds) {
    printInfo(`Not logged in to ${formatPlatformLabel(platformUrl)}.`);
    return;
  }

  if (creds.refresh_token) {
    const client = new CliApiClient({ ...base, platformUrl: creds.platform_url });
    await client.logout().catch(() => {});
  }
  await clearCredentials();
  printSuccess(`Logged out from ${formatPlatformLabel(creds.platform_url)}.`);
}

async function runLogs(
  getConfig: () => ResolvedConfig,
  opts: { pageSize?: number; cursor?: string },
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const response = await new CliApiClient(config).getRequestLogs({
    page_size: opts.pageSize,
    cursor: opts.cursor,
  });

  if (base.json) {
    printJson(response);
    return;
  }

  if (response.items.length === 0) {
    printInfo("No request logs.");
    return;
  }

  const rows = response.items.map((item) => [
    item.created_at,
    item.method,
    item.path,
    String(item.response_status ?? "-"),
    String(item.duration_ms ?? "-"),
  ]);
  printTable(["time", "method", "path", "status", "ms"], rows);
  if (response.next_cursor) {
    printInfo(`Next cursor: ${response.next_cursor}`);
  }
}

export function registerAuthCommands(program: Command, getConfig: () => ResolvedConfig): void {
  const auth = program.command("auth").description("Authentication commands");

  let login = auth
    .command("login")
    .description("Log in (default platform is production)");
  for (const [flags, desc] of LOGIN_OPTIONS) {
    login = login.option(flags, desc);
  }
  login.action(async (opts: LoginOptions) => {
    await runLogin(getConfig(), opts);
  });

  auth.command("status").description("Show login status").action(async () => {
    await runStatus(getConfig);
  });

  auth.command("refresh").description("Refresh access token").action(async () => {
    await runRefresh(getConfig);
  });

  auth.command("logout").description("Log out current account").action(async () => {
    await runLogout(getConfig);
  });

  auth
    .command("logs")
    .description("Show CLI request audit logs")
    .option("--page-size <n>", "Page size", (v) => Number.parseInt(v, 10))
    .option("--cursor <cursor>", "Cursor for next page")
    .action(async (opts: { pageSize?: number; cursor?: string }) => {
      await runLogs(getConfig, opts);
    });
}
